import { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useFeedbackStore, type FeedbackItem } from "../../stores/feedback.js";
import { FeedbackMessage } from "../feedback/FeedbackMessage.js";

const AGENT_COLORS = ["#8000ff", "#00c8ff", "#00dc64", "#ff0080", "#f59e0b", "#a855f7"];

interface AgentActivityViewProps {
  runId: string;
}

export function AgentActivityView({ runId }: AgentActivityViewProps) {
  const messagesByAgent = useFeedbackStore((s) => s.messagesByAgent);
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Group and sort agents by first activity
  const agents = useMemo(() => {
    const result: Array<{ id: string; name: string; messages: FeedbackItem[] }> = [];
    for (const [agentId, msgs] of messagesByAgent) {
      const filtered = msgs.filter((m) => m.runId === runId);
      if (filtered.length > 0) {
        result.push({
          id: agentId,
          name: msgs[0]?.source?.name ?? agentId,
          messages: filtered,
        });
      }
    }
    return result.sort(
      (a, b) =>
        new Date(a.messages[0]?.timestamp ?? 0).getTime() -
        new Date(b.messages[0]?.timestamp ?? 0).getTime(),
    );
  }, [messagesByAgent, runId]);

  // Auto-expand first agent on initial load
  useEffect(() => {
    if (!initialized && agents.length > 0) {
      setExpandedAgents(new Set([agents[0].id]));
      setInitialized(true);
    }
  }, [agents, initialized]);

  function toggleAgent(agentId: string) {
    setExpandedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }
      return next;
    });
  }

  if (agents.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-text-muted">
        Agent activity will appear when a run starts
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto">
      {agents.map((agent, i) => {
        const isExpanded = expandedAgents.has(agent.id);
        const color = AGENT_COLORS[i % AGENT_COLORS.length];
        const lastMsg = agent.messages[agent.messages.length - 1];
        const lastTime = new Date(lastMsg.timestamp).toLocaleTimeString();

        return (
          <div key={agent.id} className="border-b border-border-subtle/10">
            {/* Agent header */}
            <button
              onClick={() => toggleAgent(agent.id)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-lighter/5 transition-[color,background-color,transform] duration-200 active:scale-[0.98]"
            >
              <div
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: color }}
              />
              <div className="flex-1 min-w-0 text-left">
                <span className="text-xs font-medium text-text-primary">
                  {agent.name}
                </span>
              </div>
              <span className="text-[10px] text-text-muted font-mono tabular-nums shrink-0">
                {agent.messages.length} msg{agent.messages.length !== 1 ? "s" : ""}
              </span>
              <span className="text-[10px] text-text-muted font-mono tabular-nums shrink-0">
                {lastTime}
              </span>
              <ChevronDown
                className={`w-3.5 h-3.5 text-text-muted shrink-0 transition-transform duration-200 ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Agent messages */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div
                    className="border-l-2 ml-[18px]"
                    style={{ borderColor: color }}
                  >
                    {agent.messages.map((msg) => (
                      <FeedbackMessage key={msg.id} msg={msg} />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
